/**
 * ------------------------------------------------------------
 * - 必ず必要なもの
 * root要素
 * viewport要素
 *
 * ------------------------------------------------------------
 */

// Embla Core
import EmblaCarousel from "embla-carousel";
import type { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
// Embla Plugins
import ClassNames from 'embla-carousel-class-names';
import Autoplay, { type AutoplayOptionsType } from 'embla-carousel-autoplay';
import AutoScroll, { type AutoScrollOptionsType } from 'embla-carousel-auto-scroll';
import Fade from 'embla-carousel-fade';

// Modules
import { addPrevNextBtnsClickHandlers } from "./modules/addPrevNextBtnsClickHandlers";
import { addDotBtnsAndClickHandlers } from "./modules/addDotBtnsAndClickHandlers";
import { addThumbBtnsClickHandlers, addToggleThumbBtnsActive } from "./modules/thumbnailHandler";

// Plugins
import { emblaPluginAutoplay } from "./plugin/emblaPluginAutoplay";
import { emblaPluginAutoscroll } from "./plugin/emblaPluginAutoscroll";

/**
 * 型定義
 */
interface Options {
  embla: EmblaOptionsType;
  setPlugins: {
    classNames: boolean;
    autoplay: boolean;
    autoScroll: boolean;
    fade?: boolean;
  };
  autoplay: AutoplayOptionsType;
  autoScroll: AutoScrollOptionsType;
}

export class EmblaApp {
  // ---------- Elements ----------
  // Main Slider
  rootElement: HTMLElement;
  prevButtonElement: HTMLElement | null;
  nextButtonElement: HTMLElement | null;
  dotsElement: HTMLElement | null;


  // ---------- Embla API ----------
  // Main Slider
  emblaApi: EmblaCarouselType;
  // Thumbnail Slider

  // ---------- Constructor ----------
  constructor(
    rootElement: HTMLElement,
    options: Options,
    thumbRootElement: HTMLElement | null = null,
    thumbOptions: EmblaOptionsType | null = null,
  ) {
    //  Init Elements --------------------------------
    // Main Slider
    this.rootElement = rootElement;
    this.prevButtonElement = rootElement.querySelector(".embla__prev");
    this.nextButtonElement = rootElement.querySelector(".embla__next");
    this.dotsElement = rootElement.querySelector(".embla__dots");
    const viewportElement = rootElement.querySelector(".embla__viewport") as HTMLElement;

    //  Init Embla API --------------------------------
    // Options
    const emblaOptions = options.embla;
    // Plugins
    const setPlugins = [];
    if (options.setPlugins.classNames) setPlugins.push(ClassNames());
    if (options.setPlugins.autoplay) setPlugins.push(Autoplay(options.autoplay));
    if (options.setPlugins.autoScroll) setPlugins.push(AutoScroll(options.autoScroll));
    if (options.setPlugins.fade) setPlugins.push(Fade());

    // API
    this.emblaApi = EmblaCarousel(
      viewportElement,
      emblaOptions,
      setPlugins
    );

    //  Init --------------------------------
    this.initButtonHandler();
    this.initDotHandler();
    this.initAutoplay();
    this.initAutoScroll();

    // Thumbnail Sliderの設定がある場合は定義
    if (thumbRootElement && thumbOptions) this.initThumbnail(thumbRootElement, thumbOptions);
  }

  /**
   * Next&Prevボタンに関するイベント定義
   */
  private initButtonHandler() {
    if (!this.prevButtonElement || !this.nextButtonElement) return;

    const removePrevNextBtnsClickHandlers = addPrevNextBtnsClickHandlers(
      this.emblaApi,
      this.prevButtonElement,
      this.nextButtonElement
    );
    this.emblaApi.on('destroy', removePrevNextBtnsClickHandlers);
  }
  /**
   * Dotに関するイベントリスナーを追加
   */
  private initDotHandler() {
    if (!this.dotsElement) return;

    const removeDotBtnsAndClickHandlers = addDotBtnsAndClickHandlers(
      this.emblaApi,
      this.dotsElement
    );
    this.emblaApi.on('destroy', removeDotBtnsAndClickHandlers);
  }
  /**
  * サムネイルの設定
  */
  private initThumbnail(
    thumbRootElement: HTMLElement,
    thumbOptions: EmblaOptionsType
  ) {
    const thumbViewportElement = thumbRootElement.querySelector(".embla-thumbs__viewport") as HTMLElement;

    // Thumbnail Slider
    const thumbEmblaApi = EmblaCarousel(
      thumbViewportElement,
      thumbOptions
    );
    const removeThumbBtnsClickHandlers = addThumbBtnsClickHandlers(
      this.emblaApi,
      thumbEmblaApi
    );
    const removeToggleThumbBtnsActive = addToggleThumbBtnsActive(
      this.emblaApi,
      thumbEmblaApi
    );

    this.emblaApi
      .on('destroy', removeThumbBtnsClickHandlers)
      .on('destroy', removeToggleThumbBtnsActive);

    thumbEmblaApi
      .on('destroy', removeThumbBtnsClickHandlers)
      .on('destroy', removeToggleThumbBtnsActive);
  }

  // Plugins
  /**
   * Autoplay設定
   */
  private initAutoplay() {
    const addButtons = [];
    if (this.prevButtonElement) addButtons.push(this.prevButtonElement);
    if (this.nextButtonElement) addButtons.push(this.nextButtonElement);
    if (this.dotsElement) {
      const dotButtons = this.dotsElement.querySelectorAll('button');
      dotButtons.forEach((dotButton) => {
        addButtons.push(dotButton);
      });
    }
    emblaPluginAutoplay(this.emblaApi, addButtons);
  }
  /**
   * AutoScroll設定
   */
  private initAutoScroll() {
    emblaPluginAutoscroll(this.emblaApi);
  }
}
